import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { OpenConsumption } from "@/hooks/useOpenConsumption";

interface CancelOrphanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  consumption: OpenConsumption | null;
  onConfirm: () => void;
  isLoading?: boolean;
}

export function CancelOrphanDialog({
  open,
  onOpenChange,
  consumption,
  onConfirm,
  isLoading = false,
}: CancelOrphanDialogProps) {
  if (!consumption) return null;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Cancelar Consumo Órfão
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                Você está prestes a cancelar{" "}
                <strong>
                  {consumption.type === "table" ? "Mesa" : "Comanda"} {consumption.number}
                </strong>
                .
              </p>

              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Pedidos a cancelar</span>
                  <span className="font-medium">{consumption.ordersCount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Itens</span>
                  <span className="font-medium">{consumption.items.length}</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span>Valor total</span>
                  <span className="text-destructive">{formatCurrency(consumption.subtotal)}</span>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                <strong>Atenção:</strong> Este valor <u>não será contabilizado</u> como venda.
                Use esta opção apenas para pedidos órfãos que foram abandonados ou
                registrados incorretamente.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Voltar</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? "Cancelando..." : "Confirmar Cancelamento"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
